import { motion } from "framer-motion";
import { MicroLabel } from "@/components/decor/CropMarks";

const CATEGORIES = [
  { name: "Business Cards", tag: "Print", accent: "#0072CE" },
  { name: "Brochures", tag: "Print", accent: "#0072CE" },
  { name: "Catalogues", tag: "Print", accent: "#0072CE" },
  { name: "Packaging", tag: "Print", accent: "#0072CE" },
  { name: "Labels", tag: "Print", accent: "#0072CE" },
  { name: "Stationery", tag: "Print", accent: "#0072CE" },
  { name: "Gift Sets", tag: "Gifting", accent: "#D6007F" },
  { name: "Hampers", tag: "Gifting", accent: "#D6007F" },
  { name: "Bottles", tag: "Gifting", accent: "#D6007F" },
  { name: "Bags", tag: "Gifting", accent: "#D6007F" },
  { name: "Apparel", tag: "Gifting", accent: "#D6007F" },
  { name: "Signage", tag: "Visibility", accent: "#FFD200" },
  { name: "Vinyl", tag: "Visibility", accent: "#FFD200" },
  { name: "Standees", tag: "Visibility", accent: "#FFD200" },
  { name: "Display Systems", tag: "Visibility", accent: "#FFD200" },
  { name: "Event Branding", tag: "Visibility", accent: "#FFD200" },
  { name: "Promotional Merchandise", tag: "Brand Dev", accent: "#B89B5E" },
];

const CategoryCard = ({ c, i }) => (
  <motion.div
    data-testid={`capability-card-${i}`}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ duration: 0.5, delay: (i % 6) * 0.04 }}
    className="relative shrink-0 group cursor-default"
    style={{
      width: 260,
      height: 320,
      background: "#0A0A0A",
      border: "1px solid rgba(192,192,192,0.15)",
      transition: "transform 0.5s ease, border-color 0.5s ease",
    }}
    whileHover={{ y: -6 }}
  >
    <div
      className="absolute top-3 left-3 ko-overline"
      style={{ color: c.accent, fontSize: 10 }}
    >
      {String(i + 1).padStart(2, "0")} · {c.tag}
    </div>

    {/* abstract shape unique per index */}
    <svg viewBox="0 0 260 260" className="absolute inset-0 w-full" aria-hidden>
      <defs>
        <linearGradient id={`cap-${i}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#1c1c1c" />
          <stop offset="1" stopColor="#0a0a0a" />
        </linearGradient>
      </defs>
      <rect width="260" height="260" fill={`url(#cap-${i})`} />
      <g opacity="0.85">
        {i % 4 === 0 && (
          <>
            <rect x="40" y="60" width="180" height="120" fill="none" stroke="rgba(245,241,232,0.35)" />
            <rect x="50" y="70" width="40" height="6" fill={c.accent} />
            <rect x="50" y="84" width="120" height="3" fill="rgba(245,241,232,0.4)" />
            <rect x="50" y="94" width="80" height="3" fill="rgba(245,241,232,0.3)" />
          </>
        )}
        {i % 4 === 1 && (
          <>
            <polygon points="60,80 130,40 200,80 200,180 130,220 60,180" fill="none" stroke="rgba(245,241,232,0.4)" />
            <polygon points="60,80 130,120 200,80" fill="none" stroke="rgba(245,241,232,0.25)" />
            <line x1="130" y1="120" x2="130" y2="220" stroke="rgba(245,241,232,0.25)" />
            <rect x="100" y="150" width="60" height="6" fill={c.accent} />
          </>
        )}
        {i % 4 === 2 && (
          <>
            <rect x="50" y="40" width="160" height="180" fill="none" stroke="rgba(245,241,232,0.35)" />
            <line x1="50" y1="100" x2="210" y2="100" stroke="rgba(245,241,232,0.25)" />
            <circle cx="130" cy="160" r="32" fill="none" stroke={c.accent} />
            <line x1="98" y1="160" x2="162" y2="160" stroke={c.accent} />
            <line x1="130" y1="128" x2="130" y2="192" stroke={c.accent} />
          </>
        )}
        {i % 4 === 3 && (
          <>
            <rect x="60" y="60" width="140" height="120" fill="none" stroke="rgba(245,241,232,0.35)" />
            <rect x="60" y="170" width="140" height="40" fill="none" stroke="rgba(245,241,232,0.35)" />
            <rect x="120" y="50" width="20" height="10" fill={c.accent} />
            <rect x="80" y="80" width="40" height="60" fill="rgba(245,241,232,0.15)" />
          </>
        )}
      </g>
    </svg>

    <div
      className="absolute bottom-4 left-4 right-4 flex items-end justify-between"
      style={{ zIndex: 2 }}
    >
      <div>
        <h3
          style={{
            fontFamily: "Montserrat",
            fontWeight: 700,
            color: "#F5F1E8",
            fontSize: 18,
            lineHeight: 1.05,
          }}
        >
          {c.name}
        </h3>
        <div className="ko-overline mt-2" style={{ color: "#C0C0C0", fontSize: 9 }}>
          Capability · {String(i + 1).padStart(3, "0")}
        </div>
      </div>
      <span
        aria-hidden
        style={{ width: 10, height: 10, background: c.accent }}
      />
    </div>
  </motion.div>
);

const CapabilityUniverse = () => {
  return (
    <section
      id="solutions"
      data-testid="capability-universe"
      className="relative overflow-hidden"
      style={{ background: "#0A0A0A", paddingTop: 120, paddingBottom: 120 }}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <MicroLabel index={6} accent="#B89B5E">
              Capability Universe
            </MicroLabel>
            <h2
              className="mt-6"
              style={{
                fontFamily: "Montserrat",
                fontWeight: 800,
                color: "#F5F1E8",
                fontSize: "clamp(34px, 5vw, 64px)",
                lineHeight: 0.98,
                letterSpacing: "-0.025em",
                textTransform: "uppercase",
              }}
            >
              The ecosystem,
              <br />
              <span style={{ color: "#B89B5E" }}>laid out as a press sheet.</span>
            </h2>
          </div>
          <p
            style={{
              color: "#C0C0C0",
              maxWidth: 420,
              fontSize: 14,
              lineHeight: 1.7,
            }}
          >
            Drag, swipe, or scroll horizontally — every panel is a production-ready
            category we ship under one roof.
          </p>
        </div>
      </div>

      <div
        className="ko-lane overflow-x-auto"
        data-testid="capability-lane"
        style={{
          paddingLeft: "max(20px, calc((100vw - 1280px) / 2 + 20px))",
          paddingRight: "max(20px, calc((100vw - 1280px) / 2 + 20px))",
          paddingBottom: 20,
        }}
      >
        <div className="flex gap-5 w-max">
          {CATEGORIES.map((c, i) => (
            <CategoryCard key={c.name} c={c} i={i} />
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-12 mt-8 flex items-center justify-between">
        <div className="ko-overline" style={{ color: "#C0C0C0" }}>
          ← Drag · Scroll · Inspect →
        </div>
        <div className="ko-overline" style={{ color: "#B89B5E" }}>
          {CATEGORIES.length} categories on the sheet
        </div>
      </div>
    </section>
  );
};

export default CapabilityUniverse;
